import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle } from "lucide-react";

interface TrackingTrialModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TrackingTrialModal({ open, onOpenChange }: TrackingTrialModalProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: ""
  });

  const trialFeatures = [
    "30 dias de teste grátis",
    "Até 5 dispositivos GPS",
    "Rastreamento em tempo real",
    "Geocercas ilimitadas",
    "Relatórios básicos",
    "Suporte por e-mail"
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Lead tracking system discontinued - just show success message
      console.log('Trial request submitted:', formData);
      
      toast({
        title: "Teste iniciado!",
        description: "Verifique seu e-mail para as credenciais de acesso.",
      });
      
      onOpenChange(false);
      setFormData({
        name: "",
        email: "",
        company: "",
        phone: ""
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Erro ao iniciar teste",
        description: "Tente novamente mais tarde.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Iniciar Teste Grátis</DialogTitle>
          <DialogDescription>
            Experimente o TrackingONE por 30 dias sem compromisso
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="trial-name">Nome completo *</Label>
              <Input
                id="trial-name"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Seu nome"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trial-email">E-mail *</Label>
              <Input
                id="trial-email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="seu@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trial-company">Empresa *</Label>
              <Input
                id="trial-company"
                required
                value={formData.company}
                onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                placeholder="Nome da empresa"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trial-phone">Telefone *</Label>
              <Input
                id="trial-phone"
                required
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="(00) 00000-0000"
              />
            </div>

            <Button type="submit" disabled={loading} className="w-full bg-[#0d9488] hover:bg-[#0f766e]">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                'Iniciar Teste Grátis'
              )}
            </Button>
          </form>

          <div className="bg-muted/50 rounded-lg p-4">
            <h4 className="font-semibold mb-3 text-sm">O que está incluído:</h4>
            <ul className="space-y-2">
              {trialFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
